export type ThemePreference = "light" | "dark" | "system";

const COOKIE_NAME = "smart-theme";
const DEFAULT_PREF: ThemePreference = "system";

// Resolve public base path at runtime so deployments under subpaths
// like /SmartApp/Dev/ still load the theme bundles correctly.
// 1) Prefer webpack public path if available
// 2) Otherwise infer from the script tag that loaded our main bundle
// 3) Fallback to '/'
/* eslint-disable no-underscore-dangle */
declare const __webpack_public_path__: string | undefined;
/* eslint-enable no-underscore-dangle */

let resolvedBasePath: string | null = null;
function getBasePath(): string {
  if (resolvedBasePath) return resolvedBasePath;

  // 1) webpack public path
  try {
    const p = (typeof __webpack_public_path__ !== "undefined" && __webpack_public_path__) || "";
    if (p && p !== "auto") {
      resolvedBasePath = p.endsWith("/") ? p : `${p}/`;
      return resolvedBasePath;
    }
  } catch {
    // ignore
  }

  // 2) Infer from the script tag that loaded the app bundle
  if (typeof document !== "undefined") {
    const scripts = Array.from(document.getElementsByTagName("script"));
    // Heuristic: look for the last script whose src ends with .bundle.js or .chunk.js
    for (let i = scripts.length - 1; i >= 0; i--) {
      const src = (scripts[i] as HTMLScriptElement).src || "";
      if (/\.(bundle|chunk)\.js(\?.*)?$/.test(src)) {
        const idx = src.lastIndexOf("/");
        if (idx > -1) {
          resolvedBasePath = src.slice(0, idx + 1);
          return resolvedBasePath;
        }
      }
    }
  }

  // 3) Fallback to origin root
  resolvedBasePath = "/";
  return resolvedBasePath;
}

function withBase(p: string): string {
  const base = getBasePath();
  return `${base}${p.replace(/^\//, "")}`;
}

// CSS bundles injected at runtime by customThemeLoader (relative to public base)
const LIGHT_CSS = withBase("style.css");
const DARK_CSS = withBase("style-dark.css");
const THEME_LINK_ID = "custom-theme-stylesheet";

let systemListenerAttached = false;
let lastAppliedIsDark: boolean | null = null;

function safeDecode(v: string) {
  try {
    return decodeURIComponent(v);
  } catch {
    return v;
  }
}

function getCookieRaw(name: string): string | null {
  if (typeof document === "undefined") return null;
  const cookies = document.cookie ? document.cookie.split(";") : [];
  // If multiple cookies exist (host-only + domain cookie), prefer the most recently set.
  for (let i = cookies.length - 1; i >= 0; i--) {
    const c = cookies[i];
    const [k, ...rest] = c.trim().split("=");
    if (k === name) return safeDecode(rest.join("="));
  }
  return null;
}

function shouldShareAcrossSubdomains(hostname: string): boolean {
  const host = (hostname || "").toLowerCase();
  if (!host) return false;
  if (host === "localhost") return false;
  // IP address
  if (/^\d{1,3}(?:\.\d{1,3}){3}$/.test(host)) return false;
  // Shared only for smart-is.com and subdomains
  return host === "smart-is.com" || host.endsWith(".smart-is.com");
}

function buildCookieAttributes(): string {
  if (typeof window === "undefined") return "path=/; samesite=lax";
  const attrs: string[] = ["path=/", "samesite=lax", "max-age=31536000"]; // 1 year

  // Share across environments on smart-is.com (dev/qa/prod subdomains)
  if (shouldShareAcrossSubdomains(window.location.hostname)) {
    attrs.push("domain=.smart-is.com");
  }

  // Use Secure on https to ensure cookie is accepted in modern browsers
  if (window.location.protocol === "https:") {
    attrs.push("secure");
  }
  return attrs.join("; ");
}

function buildHostOnlyCookieAttributes(): string {
  // Same as buildCookieAttributes but without `domain=...`.
  // This ensures the current host always has an authoritative preference,
  // while we can still optionally set a shared cookie for cross-subdomain reuse.
  if (typeof window === "undefined") return "path=/; samesite=lax";
  const attrs: string[] = ["path=/", "samesite=lax", "max-age=31536000"]; // 1 year
  if (window.location.protocol === "https:") attrs.push("secure");
  return attrs.join("; ");
}

function expireCookie(name: string, attrs: string) {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=; ${attrs.replace(/max-age=\d+/i, "max-age=0")}`;
}

export function getThemePreference(): ThemePreference {
  const raw = (getCookieRaw(COOKIE_NAME) || "").trim().toLowerCase();
  if (raw === "light" || raw === "dark" || raw === "system") {
    return raw;
  }
  return DEFAULT_PREF;
}

export function setThemePreference(pref: ThemePreference) {
  if (typeof document === "undefined") return;
  const value = encodeURIComponent(pref);

  // 1) Clear old variants first to avoid multiple `smart-theme` cookies.
  // Host-only variant
  expireCookie(COOKIE_NAME, buildHostOnlyCookieAttributes());
  // Shared domain variant (only meaningful on smart-is.com)
  expireCookie(COOKIE_NAME, buildCookieAttributes());

  // 2) Set authoritative host-only cookie
  document.cookie = `${COOKIE_NAME}=${value}; ${buildHostOnlyCookieAttributes()}`;

  // 3) Also set shared cookie for smart-is.com subdomains (if applicable)
  // so other internal apps can reuse the preference.
  if (
    typeof window !== "undefined" &&
    shouldShareAcrossSubdomains(window.location.hostname)
  ) {
    document.cookie = `${COOKIE_NAME}=${value}; ${buildCookieAttributes()}`;
  }
}

function getSystemIsDark(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function ensureThemeLink(href: string) {
  if (typeof document === "undefined") return;
  let link = document.getElementById(THEME_LINK_ID) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    link.id = THEME_LINK_ID;
    link.rel = "stylesheet";
    link.type = "text/css";
    link.href = href;
    const head = document.head || document.getElementsByTagName("head")[0];
    head.appendChild(link);
    return;
  }
  if (link.getAttribute("href") !== href) {
    link.setAttribute("href", href);
  }
}

function applyResolvedTheme(isDark: boolean) {
  if (typeof document === "undefined") return;
  // Prevent noisy re-application
  if (lastAppliedIsDark === isDark) return;
  lastAppliedIsDark = isDark;

  const href = isDark ? DARK_CSS : LIGHT_CSS;
  ensureThemeLink(href);
  document.documentElement.classList.toggle("dark", isDark);
}

function detachSystemListener() {
  if (typeof window === "undefined" || !window.matchMedia) return;
  if (!systemListenerAttached) return;
  const media = window.matchMedia("(prefers-color-scheme: dark)");
  media.removeEventListener?.("change", onSystemThemeChange);
  // legacy
  // eslint-disable-next-line deprecation/deprecation
  media.removeListener?.(onSystemThemeChange as any);
  systemListenerAttached = false;
}

function attachSystemListener() {
  if (typeof window === "undefined" || !window.matchMedia) return;
  if (systemListenerAttached) return;
  const media = window.matchMedia("(prefers-color-scheme: dark)");
  if (media.addEventListener) {
    media.addEventListener("change", onSystemThemeChange);
  } else if ((media as any).addListener) {
    // eslint-disable-next-line deprecation/deprecation
    (media as any).addListener(onSystemThemeChange);
  }
  systemListenerAttached = true;
}

function onSystemThemeChange(e: MediaQueryListEvent) {
  // Only react when user preference is system
  if (getThemePreference() !== "system") return;
  applyResolvedTheme(!!e.matches);
}

export function applyThemePreference(pref: ThemePreference) {
  if (pref === "system") {
    attachSystemListener();
    applyResolvedTheme(getSystemIsDark());
    return;
  }

  // Explicit light/dark disables system listener
  detachSystemListener();
  applyResolvedTheme(pref === "dark");
}

/**
 * Initialize theme once at app startup.
 * - Reads `smart-theme` cookie
 * - Applies CSS bundle + Tailwind `.dark`
 * - Starts/stops the system listener depending on preference
 */
export function initThemeFromCookie() {
  const pref = getThemePreference();
  applyThemePreference(pref);
}
